import { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { useIsLgUp } from "@/lib/use-media-query";
import FeatureIcon from "./FeatureIcon";

function MegaLink({ item, onNavigate }) {
  const href = item.href || "#";
  const internal = href.startsWith("/") && !href.startsWith("//");
  const className =
    "group flex gap-3 rounded-lg px-2 py-2 hover:bg-[var(--bg-elev)] transition-colors";
  const close = () => onNavigate?.();
  const inner = (
    <>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[var(--border-default)] bg-[var(--bg-elev)] text-[var(--primary)] group-hover:border-[var(--primary)]/40">
        <FeatureIcon name={item.icon} size={18} />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-[var(--text-primary)] group-hover:text-[var(--primary)]">
          {item.label}
        </span>
        {item.description ? (
          <span className="block text-xs text-[var(--text-tertiary)] mt-0.5 leading-snug">{item.description}</span>
        ) : null}
      </span>
    </>
  );
  if (internal) {
    return (
      <Link to={href} className={className} onClick={close}>
        {inner}
      </Link>
    );
  }
  return (
    <a href={href} className={className} onClick={close}>
      {inner}
    </a>
  );
}

function FeaturedCard({ card, onNavigate }) {
  const href = card.href || "#";
  const internal = href.startsWith("/") && !href.startsWith("//");
  const cls = `landing-mega-featured h-full ${card.highlight ? "landing-mega-featured-highlight" : ""}`;
  const body = (
    <>
      <span className="landing-mega-featured-icon">
        <FeatureIcon name={card.icon} size={20} />
      </span>
      <span className="font-heading text-sm leading-snug text-[var(--text-primary)]">{card.title}</span>
      <span className="text-xs text-[var(--text-secondary)] leading-relaxed mt-1 line-clamp-2">{card.body}</span>
      {card.cta ? (
        <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--primary)] mt-2 inline-block">
          {card.cta} →
        </span>
      ) : null}
    </>
  );
  const close = () => onNavigate?.();
  if (internal) {
    return (
      <Link to={href} className={cls} onClick={close}>
        {body}
      </Link>
    );
  }
  return (
    <a href={href} className={cls} onClick={close}>
      {body}
    </a>
  );
}

function lockPageScroll() {
  const scrollbar = window.innerWidth - document.documentElement.clientWidth;
  document.body.style.overflow = "hidden";
  if (scrollbar > 0) document.body.style.paddingRight = `${scrollbar}px`;
}

function unlockPageScroll() {
  document.body.style.overflow = "";
  document.body.style.paddingRight = "";
}

export default function LandingMegaNav({ menu }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const isLgUp = useIsLgUp();

  useEffect(() => {
    if (!isLgUp && open) setOpen(false);
  }, [isLgUp, open]);

  useEffect(() => {
    if (!open || !isLgUp) return undefined;
    lockPageScroll();
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      unlockPageScroll();
    };
  }, [open, isLgUp]);

  if (!menu?.columns?.length) return null;

  const featured = (menu.featured || []).slice(0, 2);
  const close = () => setOpen(false);

  const dropdown =
    open && isLgUp && typeof document !== "undefined"
      ? createPortal(
          <>
            <button
              type="button"
              className="landing-mega-backdrop fixed inset-x-0 bottom-0 z-[38] border-0 cursor-default"
              style={{ top: "var(--landing-header-h)" }}
              aria-label="Close products menu"
              onClick={close}
            />
            <div
              className="fixed inset-x-0 z-[39] flex justify-center px-4 md:px-8 pointer-events-none"
              style={{ top: "calc(var(--landing-header-h) + 1px)" }}
              role="dialog"
              aria-modal="true"
              aria-label={menu.label || "Products"}
            >
              <div
                id={panelId}
                className="landing-mega-panel pointer-events-auto w-full max-w-[960px] overflow-hidden rounded-xl border border-[var(--border-default)] bg-[var(--bg-surface)] shadow-2xl"
              >
                <div className="p-6 md:p-8 grid md:grid-cols-3 gap-6 md:gap-8">
                  {menu.columns.map((col) => (
                    <div key={col.title}>
                      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--text-tertiary)] mb-3">
                        {col.title}
                      </div>
                      <div className="space-y-0.5">
                        {(col.links || []).map((link) => (
                          <MegaLink key={link.label} item={link} onNavigate={close} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {featured.length > 0 ? (
                  <div className="border-t border-[var(--border-default)] bg-[var(--bg-elev)]/50 px-6 md:px-8 py-5">
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--text-tertiary)] mb-3">
                      Highlights
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {featured.map((card) => (
                        <FeaturedCard key={card.title} card={card} onNavigate={close} />
                      ))}
                    </div>
                  </div>
                ) : null}
                {menu.seeAllHref ? (
                  <div className="px-6 md:px-8 py-3 border-t border-[var(--border-default)] bg-[var(--bg-base)]">
                    <a
                      href={menu.seeAllHref}
                      className="text-xs font-mono text-[var(--primary)] hover:underline"
                      onClick={close}
                    >
                      {menu.seeAllLabel || "See all products"} →
                    </a>
                  </div>
                ) : null}
              </div>
            </div>
          </>,
          document.body,
        )
      : null;

  return (
    <>
      <div className="relative hidden lg:block">
        <button
          type="button"
          className={`inline-flex items-center gap-1 text-sm transition-colors ${
            open ? "text-[var(--text-primary)]" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          }`}
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-controls={panelId}
          onClick={() => setOpen((v) => !v)}
        >
          {menu.label || "Products"}
          <ChevronDown size={14} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {dropdown}
    </>
  );
}
